/**
 * PHP API クライアント（Supabase の代わりにサーバーの PHP + MySQL を使用）
 */

const getApiBase = (): string => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  const base = import.meta.env.BASE_URL || '/kumachop/';
  return base.endsWith('/') ? base + 'api/' : base + '/api/';
};

const apiBase = getApiBase();

export const getMediaUrl = (path?: string): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  // apiBase usually ends with '/api/'. Replace it with '/' to access root uploads.
  const baseUrl = apiBase.replace(/\/api\/?$/, '/');
  return `${baseUrl}${path.startsWith('/') ? path.slice(1) : path}`;
};

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: string | FormData;
  headers?: Record<string, string>;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = path.startsWith('http') ? path : apiBase + path;
  const headers: Record<string, string> = {
    ...(options.headers ?? {}),
  };
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: Object.keys(headers).length ? headers : undefined,
    body: options.body,
  });
  const text = await res.text();
  let data: T;
  try {
    data = text ? (JSON.parse(text) as T) : ({} as T);
  } catch {
    throw new Error(res.ok ? text : `Request failed: ${res.status} ${text}`);
  }
  if (!res.ok) {
    const err = data as { error?: string; message?: string };
    throw new Error(err?.error ?? err?.message ?? `Request failed: ${res.status}`);
  }
  return data;
}

// --- Auth ---

export interface ApiUser {
  id: string;
  email: string;
  created_at?: string;
}

export const getCurrentUser = async (): Promise<ApiUser | null> => {
  try {
    const res = await request<{ user: ApiUser }>('auth/me.php');
    return res?.user ?? null;
  } catch {
    return null;
  }
};

export const signIn = async (
  email: string,
  password: string
): Promise<{ data: { user: ApiUser } | null; error: { message: string } | null }> => {
  try {
    const res = await request<{ data: { user: ApiUser }; error: null } | { data: null; error: { message: string } }>(
      'auth/login.php',
      { method: 'POST', body: JSON.stringify({ email, password }) }
    );
    if (res.error) return { data: null, error: res.error };
    return { data: res.data, error: null };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Login failed';
    return { data: null, error: { message } };
  }
};

export const signOut = async (): Promise<{ error: Error | null }> => {
  try {
    await request<{ error: null }>('auth/logout.php', { method: 'POST' });
    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e : new Error('Logout failed') };
  }
};

export const updatePassword = async (
  newPassword: string
): Promise<{ data: { user: { id: string } } | null; error: { message: string } | null }> => {
  try {
    const res = await request<
      { data: { user: { id: string } }; error: null } | { data: null; error: { message: string } }
    >('auth/update-password.php', {
      method: 'POST',
      body: JSON.stringify({ password: newPassword }),
    });
    if (res.error) return { data: null, error: res.error };
    return { data: res.data, error: null };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Update failed';
    return { data: null, error: { message } };
  }
};

// --- Posts ---

export const getPosts = async (params?: { status?: 'all' | 'published' | 'draft' }): Promise<unknown[]> => {
  const q = params?.status ? `?status=${params.status}` : '';
  return request<unknown[]>(`posts.php${q}`);
};

export const getPost = async (id: string): Promise<unknown> => {
  return request<unknown>(`posts.php?id=${encodeURIComponent(id)}`);
};

export const getPostsCount = async (): Promise<{ count: number }> => {
  return request<{ count: number }>('posts.php?count=1');
};

export const createPost = async (body: Record<string, unknown>): Promise<{ id: string }> => {
  return request<{ id: string }>('posts.php', { method: 'POST', body: JSON.stringify(body) });
};

export const updatePost = async (id: string, body: Record<string, unknown>): Promise<{ id: string }> => {
  return request<{ id: string }>(`posts.php?id=${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

export const deletePost = async (id: string): Promise<void> => {
  await request(`posts.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
};

// --- Portfolio ---

export const getPortfolio = async (params?: { status?: 'all' | 'published' | 'draft' }): Promise<unknown[]> => {
  const q = params?.status ? `?status=${params.status}` : '';
  return request<unknown[]>(`portfolio.php${q}`);
};

export const getPortfolioItem = async (id: string): Promise<unknown> => {
  return request<unknown>(`portfolio.php?id=${encodeURIComponent(id)}`);
};

export const getPortfolioCount = async (): Promise<{ count: number }> => {
  return request<{ count: number }>('portfolio.php?count=1');
};

export const createPortfolio = async (body: Record<string, unknown>): Promise<{ id: string }> => {
  return request<{ id: string }>('portfolio.php', { method: 'POST', body: JSON.stringify(body) });
};

export const updatePortfolio = async (id: string, body: Record<string, unknown>): Promise<{ id: string }> => {
  return request<{ id: string }>(`portfolio.php?id=${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

export const deletePortfolio = async (id: string): Promise<void> => {
  await request(`portfolio.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
};

// --- Media ---

export const getMedia = async (): Promise<unknown[]> => {
  return request<unknown[]>('media.php');
};

export const getMediaCount = async (): Promise<{ count: number }> => {
  return request<{ count: number }>('media.php?count=1');
};

export const uploadMedia = async (file: File): Promise<{ id: string; file_path: string; filename: string }> => {
  const form = new FormData();
  form.append('file', file);
  return request<{ id: string; file_path: string; filename: string }>('media.php', {
    method: 'POST',
    body: form,
  });
};

export const deleteMedia = async (id: string): Promise<void> => {
  await request(`media.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
};

// --- Contacts ---

export const getContacts = async (): Promise<unknown[]> => {
  return request<unknown[]>('contacts.php');
};

export const getContactsCount = async (): Promise<{ count: number }> => {
  return request<{ count: number }>('contacts.php?count=1');
};

// --- Settings ---

export const getSettings = async (): Promise<Record<string, string>> => {
  return request<Record<string, string>>('settings.php');
};

export const updateSettings = async (settings: Record<string, string>): Promise<void> => {
  await request('settings.php', { method: 'PUT', body: JSON.stringify(settings) });
};

export const testMail = async (debug?: boolean): Promise<{ success: boolean; message?: string; error?: string; debug?: string }> => {
  const q = debug ? 'test_mail=1&debug=1' : 'test_mail=1';
  return request<{ success: boolean; message?: string; error?: string; debug?: string }>(`settings.php?${q}`, {
    method: 'POST',
  });
};
